/*
 * Copyright (c) 2023 Bolmate B.V.
 */
import './style/bolmateSelect.scss';

import React, {
    CSSProperties, ForwardedRef, forwardRef, ReactElement, useEffect, useImperativeHandle, useMemo, useRef, useState
} from 'react';
import {AwesomeIcon, ErrorType, hasHover, remToPx} from '@dvrd/dvr-controls';
import classNames from 'classnames';
import {COLOR_BLUE_BOLMATE} from "../../../util/constants";
import {BolmateSelectItem, BolmateSelectItemValue, VATValue} from "../../../util/interfaces";
import {VATValuesMatch} from "../../../util/vatUtil";
import InfoIcon from "../../icon/infoIcon";

interface Props<RT = any> {
    onChange: (value: BolmateSelectItemValue<RT>) => void;
    onClick?: () => void;
    canOpen?: () => boolean;
    value: BolmateSelectItemValue<RT>;
    items: Array<BolmateSelectItem<RT>>;
    disabled?: boolean;
    error?: ErrorType;
    label?: string;
    className?: string;
    labelClassName?: string;
    valueClassName?: string;
    arrowClassName?: string;
    errorClassName?: string;
    itemContainerClassName?: string;
    itemClassName?: string;
    itemsPosition?: 'top' | 'bottom';
    itemsHeight?: number | string;
    initialOpen?: boolean;
    highlightSelected?: boolean;
    dynamicWidth?: boolean;
    active?: boolean;
}

function getItemLabel<RT>(item: BolmateSelectItem<RT>): string | number | ReactElement {
    const {label, valueLabel, value} = item;
    if (!!valueLabel) return valueLabel;
    else if (['string', 'number'].includes(typeof label) || React.isValidElement(label)) return label;
    return value as any;
}

export interface BolmateSelectRef {
    isOpen: () => boolean;
    onOpen: () => void;
}

function BolmateSelect<RT = any>(props: Props<RT>, ref: ForwardedRef<BolmateSelectRef>) {
    const {
        initialOpen, disabled, canOpen, onClick, onChange, value, items, active, label, labelClassName, valueClassName,
        arrowClassName, itemsHeight, itemsPosition, itemContainerClassName, itemClassName, highlightSelected, error,
        errorClassName, dynamicWidth, className
    } = props;
    const [open, setOpen] = useState(initialOpen ?? false);
    const container = useRef<HTMLDivElement>(null);
    const valueLabel = useMemo(() => {
        const item = items.find((item: BolmateSelectItem<RT>) => item.value === value);
        if (item) return getItemLabel(item);
        return '';
    }, [value, items]);
    const contentStyle = useMemo(() => {
        const style: CSSProperties = {};
        if (active) style.border = `2px solid ${COLOR_BLUE_BOLMATE}`;
        return style;
    }, [active]);

    function onClickElement() {
        if (disabled || !(canOpen?.() ?? true)) return;
        setOpen(!open);
        onClick?.();
    }

    function onClickItem(value: BolmateSelectItemValue<RT>) {
        return function(evt: React.MouseEvent) {
            evt.stopPropagation();
            onChange(value);
            setOpen(false);
        }
    }

    function onOpen() {
        setOpen(true);
    }

    function isOpen(): boolean {
        return open;
    }

    function renderLabel() {
        if (!label) return null;
        return <label className={classNames('bolmate-select-label', labelClassName)}
                      onClick={onClickElement}>{label}</label>
    }

    function renderValue() {
        return (
            <label className={classNames('bolmate-select-value', valueClassName)}
                   onClick={onClickElement}>{valueLabel}</label>
        );
    }

    function renderArrow() {
        return <AwesomeIcon name='chevron-down' onClick={onClickElement}
                            className={classNames('bolmate-select-arrow', open && 'open', arrowClassName)}/>;
    }

    function renderItems() {
        let style: CSSProperties | undefined = undefined;

        if (itemsHeight !== undefined) {
            let height: string;
            if (typeof itemsHeight === 'number') height = itemsHeight + 'px';
            else height = itemsHeight;
            style = {maxHeight: height, overflowY: 'auto'};
        }
        const _items = items.filter((item: BolmateSelectItem<RT>) => item.selectable !== false);
        return (
            <div style={style}
                 className={classNames('bolmate-select-items', (open && !disabled) && 'open', itemsPosition ?? 'bottom',
                     itemContainerClassName)}>
                {_items.map(renderItem)}
            </div>
        )
    }

    function renderItem(item: BolmateSelectItem<RT>, index: number) {
        const {label, info, disabled: itemDisabled} = item;
        let isSelected: boolean;
        if (['string', 'number'].includes(typeof value)) isSelected = item.value === value;
        else if ((item.value as any).hasOwnProperty('vat_type')) isSelected = VATValuesMatch(item.value as VATValue,
            value as VATValue);
        else isSelected = item.value === value;
        if (['number', 'string'].includes(typeof label)) return (
            <label key={index} className={classNames('bolmate-select-item', itemClassName,
                (isSelected && highlightSelected) && 'selected', itemDisabled && 'disabled')}
                   onClick={itemDisabled ? undefined : onClickItem(item.value)}>
                {label}
                {!!info && <div className='bolmate-select-item-info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <span className='bolmate-select-item-info'>{info}</span>
                </div>}
            </label>
        );
        const labelElement: ReactElement<any> = label;
        const onClick = itemDisabled ? undefined : (evt: React.MouseEvent) => {
            onClickItem(item.value)(evt);
            labelElement.props.onClick?.(evt);
        };
        const className = classNames(labelElement.props.className, 'bolmate-select-item', itemClassName,
            (isSelected && highlightSelected) && 'selected', itemDisabled && 'disabled');
        return React.cloneElement(labelElement, {...labelElement.props, onClick, className, key: index});
    }

    function renderError() {
        if (!error) return null;
        return <label className={classNames('bolmate-select-error', errorClassName)}>{error}</label>
    }

    function clickListener() {
        if (!hasHover(container.current)) setOpen(false);
    }

    function setWidth() {
        if (!dynamicWidth) return;
        const _container = container.current;
        const itemsContainer = _container?.querySelector('div.bolmate-select-items'),
            contentContainer = _container?.querySelector('div.content-container');
        if (itemsContainer && contentContainer) {
            (contentContainer as HTMLElement).style.width = (itemsContainer.getBoundingClientRect().width + remToPx(
                1.75)) + 'px';
        }
    }

    useImperativeHandle(ref, () => ({onOpen, isOpen}));

    useEffect(() => {
        window.setTimeout(() => {
            if (open) document.addEventListener('click', clickListener);
            else document.removeEventListener('click', clickListener);
        })
    }, [open]);

    useEffect(() => {
        window.setTimeout(setWidth);
    }, [items, dynamicWidth]);

    return (
        <div className={classNames('bolmate-select-container', disabled && 'disabled', open && 'open',
            !!error && 'error', dynamicWidth && 'dynamic', className)} ref={container} onClick={onClickElement}>
            {renderLabel()}
            <div className='content-container' style={contentStyle}>
                {renderValue()}
                {renderArrow()}
            </div>
            {renderItems()}
            {renderError()}
        </div>
    )
}

export default forwardRef<BolmateSelectRef, Props>(BolmateSelect);